import { environment } from "@env/environment";
import { type IBanksGetResponse } from "@interfaces/common/IBanksGetResponse";
import { type IGetIotDeviceSerialNumberResponse } from "@interfaces/common/IGetIotDeviceSerialNumberResponse";
import { type IPhoneNumbersWithServiceNumber } from "@interfaces/common/IPhoneNumbersWithServiceNumber";
import { type IRelatedTablesResponse } from "@interfaces/common/IRelatedTablesResponse";
import { type ISettings } from "@interfaces/common/ISettings";
import { type ISettingsResponse } from "@interfaces/common/ISettingsResponse";
import { type IStateResponse } from "@interfaces/common/IStateResponse";
import { type ITenantBusinessNamesResponse } from "@interfaces/common/ITenantBusinessNamesResponse";
import { type IGetAllCommunesResponse } from "@interfaces/regions-communes/IGetAllCommunesResponse";
import { type IGetAllRegionsResponse } from "@interfaces/regions-communes/IGetAllRegionsResponse";
import { lastValueFrom, retry, take, throwError, timer } from "rxjs";

import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Service } from "@angular/core";

@Service()
export class CommonService {
  private readonly httpClient = inject(HttpClient);

  public getTypeStates() {
    return this.httpClient
      .get<IStateResponse>(`${environment.BO_URL}/api/state`)
      .pipe(
        retry({
          count: 10,
          delay: (error: unknown, retryCount) => {
            const isTransient =
              error instanceof HttpErrorResponse &&
              (error.status === 0 || error.status >= 500);

            return isTransient
              ? timer(Math.min(1000 * 2 ** (retryCount - 1), 10000))
              : throwError(() => error);
          },
        })
      );
  }

  public getTenantSettings(tenant: string) {
    return this.httpClient.get<ISettingsResponse>(
      `${environment.API_URL}/api/settings/${tenant}`
    );
  }

  public updateTenantSettings(tenant: string, settings: ISettings) {
    return this.httpClient.put<ISettingsResponse>(
      `${environment.API_URL}/api/settings/${tenant}`,
      settings
    );
  }

  public getTenantBusinessNames(tenants: string[]) {
    const params = tenants.map(tenant => `tenantNames=${tenant}`).join("&");

    return this.httpClient.get<ITenantBusinessNamesResponse>(
      `${environment.API_URL}/api/common/tenancy/business-names?${params}`
    );
  }

  public getPlatformTenantBusinessNames() {
    return this.httpClient.get<ITenantBusinessNamesResponse>(
      `${environment.API_URL}/api/common/tenancy/business-names/platform`
    );
  }

  public getRelatedTables(tenant: string, nameTable: string, id: number) {
    return this.httpClient.get<IRelatedTablesResponse>(
      `${environment.API_URL}/api/Common/${tenant}/relations/${nameTable}/${id}`
    );
  }

  public async getPhoneNumbersByServiceNumber(
    tenant: string,
    serviceNumber: number[]
  ) {
    const params = serviceNumber.map(sn => `serviceMeterIds=${sn}`).join("&");
    const request$ = this.httpClient
      .get<IPhoneNumbersWithServiceNumber>(
        `${environment.API_URL}/api/Common/${tenant}/phone-numbers?${params}`
      )
      .pipe(take(1));
    return await lastValueFrom<IPhoneNumbersWithServiceNumber>(request$);
  }

  getRegions() {
    return this.httpClient.get<IGetAllRegionsResponse>(
      `${environment.API_URL}/api/geo/regions/`
    );
  }

  async getAllCommunes() {
    const request = this.httpClient
      .get<IGetAllCommunesResponse>(`${environment.API_URL}/api/geo/communes`)
      .pipe(take(1));

    return await lastValueFrom<IGetAllCommunesResponse>(request);
  }

  getIotDeviceSerialNumber(tenant: string, devEui: string) {
    return this.httpClient.get<IGetIotDeviceSerialNumberResponse>(
      `${environment.API_URL}/api/iot/${tenant}/smart-meters/${devEui}/serial-number`
    );
  }

  getBanks() {
    return this.httpClient.get<IBanksGetResponse>(
      `${environment.API_URL}/api/common/banks`
    );
  }
}
